// React
import { ReactNode } from "react"

// Translations
import { useTrans } from "@/i18n"

// UI
import { PageTemplate, PageTemplateLink } from "@/layouts/PageTemplate"

// Hooks
import { useCurrentProject } from "./hooks"

// Pages
import { Routes } from "@/constants/routes"

import { ProjectStateEnum } from "@/api/graphql"

// Feature flags
import { useFeatureFlags } from "@/context/user"

/**
 * ProjectSubMenuTabs
 * @returns
 */
function ProjectSubMenuTabs() {
	const { getFeatureFlagValue } = useFeatureFlags()
	const { data } = useCurrentProject()
	const t = useTrans("project")

	return (
		<>
			<PageTemplateLink
				to={`${Routes.InvestmentsProjects}/${data?.project?.id}/dashboard`}
				end
				data-testid="project.dashboard"
				name={t("project:project.dashboard.title")}
				analyticsId="project.dashboard"
			/>
			<PageTemplateLink
				to={`${Routes.InvestmentsProjects}/${data?.project?.id}/revenue`}
				data-testid="project.revenue"
				name={t("project:project.revenue.title")}
				analyticsId="project.revenue"
			/>

			{/** Show production when project has Production data */}
			{data?.project?.state === ProjectStateEnum.InProduction ? (
				<PageTemplateLink
					to={`${Routes.InvestmentsProjects}/${data?.project?.id}/data`}
					data-testid="project.data"
					name={t("project:project.data.title")}
					analyticsId="project.data"
				/>
			) : null}

			<PageTemplateLink
				to={`${Routes.InvestmentsProjects}/${data?.project?.id}/notifications`}
				data-testid="project.notifications"
				name={t("project:project.notifications.title")}
				analyticsId="project.notifications"
			/>
			{getFeatureFlagValue("FEATURE_ENABLE_PROJECT_TIMELINE") ===
				true && (
				<PageTemplateLink
					to={`${Routes.InvestmentsProjects}/${data?.project?.id}/communications`}
					data-testid="project.communications"
					name={t("project:project.communications.title")}
					analyticsId="project.communications"
				/>
			)}

			<PageTemplateLink
				to={`${Routes.InvestmentsProjects}/${data?.project?.id}/documentation`}
				data-testid="project.documentation"
				name={t("project:project.documentation.title")}
				analyticsId="project.documentation"
			/>
		</>
	)
}

/**
 * ProjectLayout
 * @param {ReactNode} children
 * @returns
 */
export const ProjectLayout = ({ children }: { children: ReactNode }) => {
	const { data } = useCurrentProject()
	const t = useTrans("project")

	return (
		<>
			<title>
				{t("common:common.web.title", {
					page: data?.project?.name,
				})}
			</title>
			<PageTemplate
				title={data?.project?.name ?? ""}
				backHref={Routes.InvestmentsProjects}
				tabs={<ProjectSubMenuTabs />}
			>
				{children}
			</PageTemplate>
		</>
	)
}
